'use client'

import { useEffect, useRef } from 'react'
import { FishingDayPhase } from '@/lib/fishing-day-phase'
import styles from './FishingScene.module.css'

const BACKGROUNDS: Record<FishingDayPhase, string> = {
  [FishingDayPhase.Night]: '/fishing/lake-night.png',
  [FishingDayPhase.Dawn]: '/fishing/lake-dawn.png',
  [FishingDayPhase.Morning]: '/fishing/lake-morning.png',
  [FishingDayPhase.Day]: '/fishing/lake-day.png',
  [FishingDayPhase.LateAfternoon]: '/fishing/lake-late-afternoon.png',
  [FishingDayPhase.Sunset]: '/fishing/lake-sunset.png',
}

const VERTEX_SHADER = `
  attribute vec2 a_position;
  varying vec2 v_uv;
  void main() {
    v_uv = a_position * 0.5 + 0.5;
    gl_Position = vec4(a_position, 0.0, 1.0);
  }
`

const FRAGMENT_SHADER = `
  precision mediump float;
  uniform sampler2D u_image;
  uniform sampler2D u_mask;
  uniform float u_time;
  uniform vec2 u_coverScale;
  varying vec2 v_uv;

  void main() {
    float screenY = 1.0 - v_uv.y;
    vec2 baseUv = (v_uv - 0.5) * u_coverScale + 0.5;
    float waterMask = smoothstep(0.12, 0.88, texture2D(u_mask, baseUv).r);
    if (waterMask < 0.001) discard;

    vec2 sampleUv = baseUv;
    float broadWave = sin(screenY * 118.0 + u_time * 1.25) * 0.00215;
    float fineWave = sin(screenY * 247.0 - u_time * 1.7) * 0.0009;
    float crossWave = sin(v_uv.x * 31.0 + u_time * 0.72) * 0.00072;
    float verticalWave = sin(v_uv.x * 18.0 - u_time * 0.9) * 0.00115;
    float centerX = 1.0 - smoothstep(0.08, 0.48, abs(v_uv.x - 0.56));
    float centerY = smoothstep(0.34, 0.52, screenY)
      * (1.0 - smoothstep(0.88, 1.0, screenY));
    float motionStrength = 1.15 + centerX * centerY * 0.85;
    sampleUv.x += (broadWave + fineWave) * motionStrength;
    sampleUv.y += (crossWave + verticalWave) * motionStrength;

    vec4 color = texture2D(u_image, sampleUv);
    float alpha = waterMask * 0.42;
    gl_FragColor = vec4(color.rgb * alpha, alpha);
  }
`

function compileShader(gl: WebGLRenderingContext, type: number, source: string) {
  const shader = gl.createShader(type)
  if (!shader) return null
  gl.shaderSource(shader, source)
  gl.compileShader(shader)
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader)
    return null
  }
  return shader
}

export function WaterShader({ dayPhase }: { dayPhase: FishingDayPhase }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || typeof WebGLRenderingContext === 'undefined') return
    const gl = canvas.getContext('webgl', { alpha: true, antialias: false })
    if (!gl) return

    const vertex = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER)
    const fragment = compileShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER)
    if (!vertex || !fragment) return
    const program = gl.createProgram()
    if (!program) return
    gl.attachShader(program, vertex)
    gl.attachShader(program, fragment)
    gl.linkProgram(program)
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return
    gl.useProgram(program)

    const buffer = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW)
    const position = gl.getAttribLocation(program, 'a_position')
    gl.enableVertexAttribArray(position)
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0)

    const texture = gl.createTexture()
    const maskTexture = gl.createTexture()
    if (!texture || !maskTexture) return
    const configureTexture = (unit: number, target: WebGLTexture) => {
      gl.activeTexture(unit)
      gl.bindTexture(gl.TEXTURE_2D, target)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
    }
    configureTexture(gl.TEXTURE0, texture)
    configureTexture(gl.TEXTURE1, maskTexture)
    gl.uniform1i(gl.getUniformLocation(program, 'u_image'), 0)
    gl.uniform1i(gl.getUniformLocation(program, 'u_mask'), 1)
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 1)

    let frame = 0
    let imageReady = false
    let maskReady = false
    const coverScaleLocation = gl.getUniformLocation(program, 'u_coverScale')
    const image = new Image()
    image.decoding = 'async'
    image.onload = () => {
      gl.activeTexture(gl.TEXTURE0)
      gl.bindTexture(gl.TEXTURE_2D, texture)
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image)
      imageReady = true
      resize()
    }
    image.src = BACKGROUNDS[dayPhase]

    const maskImage = new Image()
    maskImage.decoding = 'async'
    maskImage.onload = () => {
      gl.activeTexture(gl.TEXTURE1)
      gl.bindTexture(gl.TEXTURE_2D, maskTexture)
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, maskImage)
      maskReady = true
    }
    maskImage.src = '/fishing/lake-water-mask.png'

    const resize = () => {
      const ratio = Math.min(window.devicePixelRatio || 1, 1.5)
      const width = Math.max(1, Math.round(canvas.clientWidth * ratio))
      const height = Math.max(1, Math.round(canvas.clientHeight * ratio))
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width
        canvas.height = height
      }
      gl.viewport(0, 0, width, height)
      if (imageReady && coverScaleLocation) {
        const canvasAspect = width / height
        const imageAspect = image.naturalWidth / image.naturalHeight
        const scaleX = canvasAspect < imageAspect ? canvasAspect / imageAspect : 1
        const scaleY = canvasAspect > imageAspect ? imageAspect / canvasAspect : 1
        gl.uniform2f(coverScaleLocation, scaleX, scaleY)
      }
    }
    const observer = new ResizeObserver(resize)
    observer.observe(canvas)
    resize()

    const timeLocation = gl.getUniformLocation(program, 'u_time')
    const render = (time: number) => {
      resize()
      gl.clearColor(0, 0, 0, 0)
      gl.clear(gl.COLOR_BUFFER_BIT)
      if (imageReady && maskReady) {
        gl.uniform1f(timeLocation, time / 1000)
        gl.drawArrays(gl.TRIANGLES, 0, 6)
      }
      frame = window.requestAnimationFrame(render)
    }
    frame = window.requestAnimationFrame(render)

    return () => {
      window.cancelAnimationFrame(frame)
      observer.disconnect()
      gl.deleteTexture(texture)
      gl.deleteTexture(maskTexture)
      gl.deleteBuffer(buffer)
      gl.deleteProgram(program)
      gl.deleteShader(vertex)
      gl.deleteShader(fragment)
    }
  }, [dayPhase])

  return <canvas ref={canvasRef} className={styles.waterShader} aria-hidden="true" />
}
